import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity } from 'react-native';
import { WifiOff, X } from 'lucide-react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { palette, spacing, radius, typography } from '@/styles/tokens';

interface NetworkConnectionModalProps {
  visible: boolean;
  onClose?: () => void;
}

export const NetworkConnectionModal: React.FC<NetworkConnectionModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(false);

  // Reset dismissed state when connection is restored
  useEffect(() => {
    if (!visible) {
      setIsDismissed(false);
    }
  }, [visible]);

  const handleClose = () => {
    console.log('🌐 [NETWORK-MODAL] User dismissed network connection modal');
    setIsDismissed(true);
    if (onClose) {
      onClose();
    }
  };

  // Don't show modal if user dismissed it (until connection is restored)
  const shouldShowModal = visible && !isDismissed;

  return (
    <Modal
      visible={shouldShowModal}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityLabel="Fermer"
            accessibilityRole="button"
          >
            <X size={24} color="#9CA3AF" strokeWidth={2} />
          </TouchableOpacity>

          <View style={styles.iconContainer}>
            <WifiOff size={64} color="#EF4444" strokeWidth={2} />
          </View>

          <Text style={styles.title}>{t('connectionRequired')}</Text>

          <Text style={styles.message}>
            {t('noInternetConnection')}
          </Text>

          <View style={styles.statusIndicator}>
            <View style={styles.statusDot} />
            <Text style={styles.statusText}>{t('noInternetConnectionShort')}</Text>
          </View>

          <TouchableOpacity
            style={styles.dismissButton}
            onPress={handleClose}
            activeOpacity={0.7}
          >
            <Text style={styles.dismissButtonText}>{t('dismiss') || 'Continuer hors ligne'}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  modalContainer: {
    backgroundColor: '#1F2937',
    borderRadius: spacing.lg,
    padding: spacing.xxl,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    shadowColor: palette.black,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: radius.xl,
    elevation: 8,
  },
  iconContainer: {
    marginBottom: spacing.xl,
    padding: spacing.md,
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderRadius: 50,
  },
  title: {
    fontSize: 22,
    fontWeight: typography.weight.bold,
    color: palette.white,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  message: {
    fontSize: typography.fontSize.base,
    color: '#D1D5DB',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.xl,
  },
  statusIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    paddingHorizontal: spacing.md,
    paddingVertical: 10,
    borderRadius: radius.lg,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: radius.sm,
    backgroundColor: palette.semantic.error,
    marginRight: spacing.xs,
  },
  statusText: {
    fontSize: typography.fontSize.md,
    color: palette.semantic.error,
    fontWeight: typography.weight.semibold,
  },
  closeButton: {
    position: 'absolute',
    top: spacing.md,
    right: spacing.md,
    zIndex: 10,
    padding: spacing.xs,
    borderRadius: spacing.lg,
    backgroundColor: 'rgba(156, 163, 175, 0.1)',
  },
  dismissButton: {
    marginTop: spacing.xl,
    backgroundColor: '#4B5563',
    paddingVertical: 14,
    paddingHorizontal: spacing.xxl,
    borderRadius: radius.lg,
    width: '100%',
    alignItems: 'center',
  },
  dismissButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
    color: palette.white,
  },
});
